// app/src/features/tracking/TrackingBlocks.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import { C } from "../../lib/colors.js";
import { findEntryByMoment } from "../../lib/forecast/reforecastEntries.js";
import { fmtResolveDate } from "../../lib/formatDates.js";
import { hoverAnchor, ui } from "../../lib/telemetry/instrument.js";
import { AXIS_H } from "../../lib/timeline/geometry.js";
import { LATEST_CARD_W } from "../../lib/timeline/latestMark.js";
import { markKey } from "../../lib/timeline/markKey.js";

// Code-split like the report modal (ReportHost.jsx): it loads on the first open, off the main
// chunk whose cap lives in app/bundle-budget.json.
const LatestForecastCard = lazy(() => import("./components/LatestForecastCard.jsx"));

/**
 * The frame's `trackingMarks` slot: the Forecast Tracking blocks over the timeline — a guide line
 * per forecast mark that grows in while the mark is active, the click band over the active mark
 * that views the forecast as of that moment, and the latest forecast's card, which that band opens
 * on the latest mark while the latest forecast is the one on screen. All are gated on
 * `forecastTracking` (the deployment bit; a DISPLAY gate). A desktop slot: `AppFrame` mounts
 * `PhoneApp` alone on a phone and never renders it there.
 */
export default function TrackingBlocks({ ctx }) {
  const {
    forecastTracking,
    searchMoments,
    fcMarkX,
    vw,
    activeMarkIso,
    tsdH,
    signpostsH,
    hov,
    reforecastEntries,
    liveSentinelIso,
    scrubToMoment,
    forecastHeaderH,
    active,
    viewMomentIso,
    viewPrevVersion,
    effectiveToday,
    getTrackingPrefs,
    openReport,
    sayerCopy,
    moveForecastToTop,
  } = ctx;
  // Whether the card is open, for the forecast on screen. Another forecast (or none) closes
  // it, so returning to this one doesn't reopen it.
  const activeId = active?.id ?? null;
  const [card, setCard] = useState({ forecastId: activeId, open: false });
  if (card.forecastId !== activeId) setCard({ forecastId: activeId, open: false });
  const viewingLatest = !viewMomentIso || viewMomentIso === liveSentinelIso;
  const cardX = liveSentinelIso ? fcMarkX(liveSentinelIso) : null;
  const cardOpen =
    forecastTracking &&
    !!active &&
    card.open &&
    viewingLatest &&
    cardX != null &&
    cardX >= -10 &&
    cardX <= vw + 10;
  const closeCard = () => setCard({ forecastId: activeId, open: false });
  // Escape or a press anywhere but the card or the mark closes it. Listened for here rather than
  // in the card, so a dismissal while the card's code is still loading counts.
  useEffect(() => {
    if (!cardOpen) return undefined;
    const onEvent = (e) => {
      const keep =
        e.type === "keydown"
          ? e.key !== "Escape"
          : e.target?.closest?.(
              '[data-surface="latest-forecast"], [data-ui="timeline.forecast-mark"]',
            );
      if (!keep) setCard((c) => ({ ...c, open: false }));
    };
    // telemetry-gesture: latest-forecast.close
    document.addEventListener("keydown", onEvent);
    // Capture: the date axis and the trigger circles stop a press from bubbling.
    // telemetry-gesture: latest-forecast.close
    document.addEventListener("mousedown", onEvent, true);
    return () => {
      document.removeEventListener("keydown", onEvent);
      document.removeEventListener("mousedown", onEvent, true);
    };
  }, [cardOpen]);
  // Above the date axis: past the signposts, the signals chart, its 4 px handle and the axis.
  const cardBottom = signpostsH + tsdH + 4 + AXIS_H + 10;
  return (
    <>
      {/* A per-mark element with a CSS transition (rather than a keyframe on a single
       mounted element) animates BOTH the grow-in on hover AND the collapse-out when the
       cursor leaves. It grows from / collapses to the hash (transform-origin at the
       hash's vertical center, `tsdH + handle(4) + AXIS_H/2` up from the bottom). */}
      {forecastTracking &&
        searchMoments.map((iso, i) => {
          const gx = fcMarkX(iso);
          if (gx == null || gx < -10 || gx > vw + 10) return null;
          // The latest mark keeps its line while its card is open, tying the card to it.
          const on = activeMarkIso === iso || (cardOpen && iso === liveSentinelIso);
          const originFromTop = `calc(100% - ${tsdH + 4 + AXIS_H / 2}px)`;
          return (
            <div
              key={`fc-guide-${i}`}
              {...hoverAnchor("timeline.forecast-mark", markKey(iso))}
              style={{
                position: "absolute",
                left: gx,
                top: 0,
                bottom: signpostsH,
                width: 2,
                background: C.action,
                opacity: on ? 0.7 : 0,
                transform: `translateX(-50%) scaleY(${on ? 1 : 0})`,
                transformOrigin: `center ${originFromTop}`,
                transition: "transform 0.3s ease-out, opacity 0.3s ease-out",
                pointerEvents: "none",
                zIndex: 14,
              }}
            />
          );
        })}
      {/* Suppressed while a pie/trigger is hovered (`hov`) so the band never intercepts a
       click meant for the pie, even when scrubbing (activeMarkIso falls back to the
       viewed snapshot). Kept separate from the visual lines above. */}
      {forecastTracking &&
        !hov &&
        activeMarkIso &&
        (() => {
          const gx = fcMarkX(activeMarkIso);
          if (gx == null || gx < -10 || gx > vw + 10) return null;
          // Label by mark KIND, not by matching the list tail: the tail is a resolution on
          // resolved questions, so matching it would mislabel the mark.
          const _markEntry = findEntryByMoment(reforecastEntries, activeMarkIso);
          const isLatest = activeMarkIso === liveSentinelIso;
          // Already on screen, the latest forecast has nothing to go back to: its mark opens
          // its card instead.
          const opensCard = isLatest && viewingLatest && !!active;
          const markTitle = _markEntry?.isResolution
            ? `Resolution as of ${fmtResolveDate(activeMarkIso)}`
            : opensCard
              ? "About the latest forecast"
              : isLatest
                ? "Back to the latest forecast"
                : `Click to view forecast as of ${fmtResolveDate(activeMarkIso)}`;
          return (
            <div
              key="fc-guide-hit"
              {...ui("timeline.forecast-mark", markKey(activeMarkIso))}
              onClick={(e) => {
                e.stopPropagation();
                if (opensCard) {
                  setCard({ forecastId: activeId, open: !cardOpen });
                  return;
                }
                // Closed, not just hidden: otherwise returning to the latest would reopen it.
                closeCard();
                // scrubToMoment resolves the live marker → latest itself.
                scrubToMoment(activeMarkIso);
              }}
              title={markTitle}
              style={{
                position: "absolute",
                left: gx - 6,
                top: forecastHeaderH,
                bottom: signpostsH,
                width: 12,
                cursor: "pointer",
                zIndex: 15,
              }}
            />
          );
        })()}
      {cardOpen && (
        <Suspense fallback={null}>
          <LatestForecastCard
            forecast={active}
            at={liveSentinelIso}
            previous={reforecastEntries.length ? viewPrevVersion : undefined}
            nowMs={effectiveToday.getTime()}
            cadIdx={getTrackingPrefs(active.id).cadIdx}
            onOpenReport={() => {
              closeCard();
              openReport(true);
            }}
            onUpdateNow={() => {
              closeCard();
              sayerCopy.updateNow(active);
              moveForecastToTop(active.id);
            }}
            onClose={closeCard}
            // From just left of the mark, kept inside the timeline.
            left={Math.max(8, Math.min(cardX - 24, vw - LATEST_CARD_W - 8))}
            bottom={cardBottom}
            // No taller than the room below the header; a tall signals chart leaves little.
            maxHeight={`calc(100% - ${cardBottom + forecastHeaderH + 8}px)`}
          />
        </Suspense>
      )}
    </>
  );
}
